import { dateFormatConverter, getCurrentCTDateTime } from '../../../../helpers';
import { API_DATE_FORMAT, DATE_DISPLAY_FORMAT, InvoiceStatus } from '../../../../constants';
import { InvoicePaymentStatusEnum } from '../claim-info/claim-payments/constants';

import type { IInvoiceHistoryTableRowData } from './InvoiceHistoryTableRow';
import type {
  IClaimInvoice,
  IClaimInvoiceHistory,
  IClaimMinified,
  IInvoiceDetails,
} from '../../../../utils/types';

interface ClaimTotals {
  billed: number;
  insPaid: number;
  adjustment: number;
  deductible: number;
  coIns: number;
  coPay: number;
  balanceDue: number;
  discount: number | null;
}

export function filterAndTransformInvoiceTableData(
  data: IClaimInvoiceHistory[] = [],
): IInvoiceHistoryTableRowData[] {
  return data
    .filter((row) => row.invoice_id === null)
    .map((row) => {
      return {
        invoice_id: row.invoice_id,
        is_invoiceable: row.is_invoiceable,
        datesOfService: `${dateFormatConverter(
          row.earliest_date_of_service,
          API_DATE_FORMAT,
          DATE_DISPLAY_FORMAT,
        )} - ${dateFormatConverter(
          row.latest_date_of_service,
          API_DATE_FORMAT,
          DATE_DISPLAY_FORMAT,
        )}`,
        id: row.id,
        code: row.service_code?.code,
        patient_responsibility: row.patient_responsibility,
      };
    });
}

export function transformInvoiceClaimDetailsTableData(data: IClaimMinified[] = []) {
  return data.map((row) => ({
    datesOfService: `${dateFormatConverter(
      row.earliest_date_of_service,
      API_DATE_FORMAT,
      DATE_DISPLAY_FORMAT,
    )} - ${dateFormatConverter(row.latest_date_of_service, API_DATE_FORMAT, DATE_DISPLAY_FORMAT)}`,
    ...row,
  }));
}

export const calculateClaimsAndPaymentTotal = (
  invoiceData?: IInvoiceDetails | null,
): [ClaimTotals, number] => {
  const data: ClaimTotals = {
    billed: 0,
    insPaid: 0,
    adjustment: 0,
    deductible: 0,
    coIns: 0,
    coPay: 0,
    balanceDue: 0,
    discount: null,
  };
  let paymentTotal = 0;
  if (!invoiceData) return [data, paymentTotal];
  invoiceData.claims?.forEach((claim) => {
    data.billed += claim.billed;
    data.insPaid += claim.amount_insurance_paid;
    data.adjustment += claim.adjustments;
    data.deductible += claim.deductible;
    data.coIns += claim.co_insurance;
    data.coPay += claim.co_pay;
    data.balanceDue += claim.patient_responsibility;
  });
  data.discount = invoiceData.discount ?? null;
  paymentTotal =
    invoiceData.payments
      ?.filter((payment) => payment.status === InvoicePaymentStatusEnum.SUCCESS)
      .reduce((sum, payment) => (payment.amount ?? 0) + sum, 0) || 0;
  return [data, paymentTotal];
};

export const sortInvoicesByStatus = (invoices: IClaimInvoice[]): IClaimInvoice[] => {
  const statusOrder = {
    [InvoiceStatus.NOT_PAID]: 1,
    [InvoiceStatus.PARTIALLY_PAID]: 2,
    [InvoiceStatus.PAID]: 3,
    [InvoiceStatus.IN_PROGRESS]: 4,
  };

  return Array.from(invoices ?? []).sort((a: IClaimInvoice, b: IClaimInvoice) => {
    return statusOrder[a.status] - statusOrder[b.status];
  });
};

export const getDueInDaysLabel = (days: number) => {
  return `${getCurrentCTDateTime().add(days, 'days').format(DATE_DISPLAY_FORMAT)}`;
};

// Scale through exponential form so half-cents such as 1.005 are not lost to binary float error.
// Residues below 1e-6 stringify in scientific notation ("3.55e-15e2" is NaN) and round to 0 cents.
export function roundToCents(amount: number): number {
  const absoluteAmount = Math.abs(amount);
  if (absoluteAmount < 1e-6) {
    return 0;
  }

  return Math.sign(amount) * Number(Math.round(Number(`${absoluteAmount}e2`)) + 'e-2');
}
